import { Link, useNavigate } from 'react-router';

import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';

import { glacierPolicyCloneApiCopyPolicyEndpointMutation } from '@/lib/heyapi/@tanstack/react-query.gen';
import type { Policy } from '@/lib/heyapi/types.gen';

import { formatErrorMessage } from '../../utils';
import type { Message } from '../StandardMessage';

/**
 * Copy Quote dialog (DC-3396, simplified in DC-3690): makes a new,
 * independent quote from the current quote.
 *
 * A single confirm-and-copy action: a read-only source summary plus Cancel /
 * Copy Quote. Clone mode and transaction range are no longer exposed -- the
 * backend always runs a full clone of the quote's single transaction, excludes
 * generated documents (the new quote regenerates its own) and keeps
 * correspondence. On success it navigates to the copied quote and shows a toast
 * with the new quote number and a link (the toast survives the SPA navigation);
 * on error it keeps the dialog open so it can be retried.
 *
 * @param accountId - Source quote id; used for the copy mutation.
 * @param isOpen - Controlled dialog visibility.
 * @param onOpenChange - Owner's dialog state setter; close requests are ignored mid-copy.
 * @param policy - Source quote used for the read-only summary header.
 * @param setMessage - Header banner setter for the "Copying quote..." progress note.
 */
export const CopyQuote = ({
  accountId,
  isOpen,
  onOpenChange,
  policy,
  setMessage,
}: {
  accountId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  policy: Policy | undefined;
  setMessage: (message: Message | undefined) => void;
}) => {
  const navigate = useNavigate();

  const { mutate: copyQuote, isPending: isCopying } = useMutation({
    ...glacierPolicyCloneApiCopyPolicyEndpointMutation(),
    onSuccess(data) {
      setMessage(undefined);
      onOpenChange(false);
      // SPA navigation keeps the toast (and its link) visible on the new
      // page — unlike a hard window.location redirect.
      toast({
        title: 'Quote Copied',
        description: (
          <div>
            <p>New Quote #: {data.quoteNumber}</p>
            <Link
              to={`/policy/${data.newPolicyId}/policy-details`}
              className="font-medium underline"
            >
              Open the new quote
            </Link>
          </div>
        ),
      });
      navigate(`/policy/${data.newPolicyId}/policy-details`);
    },
    onError(error) {
      setMessage(undefined);
      // Deliberately keep the dialog open (unlike sibling dialogs) so the
      // user can retry.
      toast({
        title: 'Error Copying Quote',
        description: formatErrorMessage(
          error,
          'An error occurred while copying the quote'
        ),
      });
    },
  });

  // Block ESC / X / overlay closes while the copy is in flight -- the
  // mutation would complete anyway and navigate a user who thinks they
  // aborted.
  const handleOpenChange = (open: boolean) => {
    if (!open && isCopying) {
      return;
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Copy Quote</DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 rounded-md border p-3 text-sm">
            <div className="flex flex-col gap-0.5">
              <span className="text-muted-foreground">Quote Number</span>
              <span className="truncate font-bold">
                {policy?.policyNumber || '—'}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-muted-foreground">Agency</span>
              <span className="truncate font-bold">
                {policy?.details?.agencyName || '—'}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-muted-foreground">First Named Insured</span>
              <span className="truncate font-bold">
                {policy?.firstNamedInsured || '—'}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCopying}
          >
            Cancel
          </Button>
          <Button
            disabled={isCopying}
            loading={isCopying}
            onClick={() => {
              setMessage({ content: 'Copying quote...', level: 'INFO' });
              copyQuote({ path: { account_id: accountId } });
            }}
          >
            Copy Quote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
